import React, { useState } from 'react';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Divider, Button, IconButton
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Lock as LockIcon, Edit as EditIcon, Warning as WarningIcon } from '@mui/icons-material';
import ESocialReferenceAutocomplete from './ESocialReferenceAutocomplete';
import type { S2240Metadata } from '../../../../../types/esocial-forms';

interface Props {
  metadata: Partial<S2240Metadata> | null;
  onChange: (metadata: Partial<S2240Metadata>) => void;
  gaps?: string[];
  overrideEnabled?: boolean;
}

const UF_OPTIONS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

const S2240Form: React.FC<Props> = ({ metadata, onChange, gaps = [], overrideEnabled = false }) => {
  const [isOverride, setIsOverride] = useState(false);
  const info = metadata?.infoExpRisco ?? { dtIniCondicao: '', agNoc: [], respReg: [] };
  const agNoc = info.agNoc ?? [];
  const respReg = info.respReg ?? [];

  const updateInfo = (field: string, value: unknown) => {
    onChange({
      ...metadata,
      infoExpRisco: { ...info, [field]: value }
    } as Partial<S2240Metadata>);
  };

  const updateInfoAmb = (field: string, value: unknown) => {
    onChange({
      ...metadata,
      infoExpRisco: { ...info, infoAmb: { ...info.infoAmb, codAmb: info.infoAmb?.codAmb ?? '', [field]: value } }
    } as Partial<S2240Metadata>);
  };

  // Agentes Nocivos
  const addAgNoc = () => {
    updateInfo('agNoc', [...agNoc, { codAgNoc: '', epcEpi: { utilizEPC: 0, utilizEPI: 0 } }]);
  };

  const removeAgNoc = (idx: number) => {
    updateInfo('agNoc', agNoc.filter((_, i) => i !== idx));
  };

  const updateAgNoc = (idx: number, field: string, value: unknown) => {
    const updated = [...agNoc];
    updated[idx] = { ...updated[idx]!, [field]: value };
    updateInfo('agNoc', updated);
  };

  const updateAgNocEpcEpi = (idx: number, field: string, value: unknown) => {
    const updated = [...agNoc];
    const current = updated[idx]!;
    updated[idx] = { ...current, epcEpi: { ...current.epcEpi, utilizEPC: current.epcEpi?.utilizEPC ?? 0, utilizEPI: current.epcEpi?.utilizEPI ?? 0, [field]: value } };
    updateInfo('agNoc', updated);
  };

  // Responsaveis Tecnicos
  const addResp = () => {
    updateInfo('respReg', [...respReg, { cpfResp: '', nmResp: '', ideOC: 1, nrOC: '' }]);
  };

  const removeResp = (idx: number) => {
    updateInfo('respReg', respReg.filter((_, i) => i !== idx));
  };

  const updateResp = (idx: number, field: string, value: unknown) => {
    const updated = [...respReg];
    updated[idx] = { ...updated[idx]!, [field]: value };
    updateInfo('respReg', updated);
  };

  return (
    <Box>
      {/* Review mode header */}
      {gaps.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" fontSize="small" />
          <Typography variant="body2" color="warning.main">
            {gaps.length} campo(s) pendente(s) de preenchimento
          </Typography>
          {overrideEnabled && (
            <Button
              size="small"
              variant={isOverride ? 'contained' : 'outlined'}
              startIcon={isOverride ? <LockIcon /> : <EditIcon />}
              onClick={() => setIsOverride(!isOverride)}
              sx={{ ml: 'auto' }}
            >
              {isOverride ? 'Bloquear campos' : 'Editar todos os campos'}
            </Button>
          )}
        </Box>
      )}

      {/* Condicao */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Condicao Ambiental</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth type="date" label="Data Inicio Condicao *" value={info.dtIniCondicao || ''} onChange={(e) => updateInfo('dtIniCondicao', e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
      </Grid>

      {/* Ambiente */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Ambiente de Trabalho</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Codigo do Ambiente" value={info.infoAmb?.codAmb || ''} onChange={(e) => updateInfoAmb('codAmb', e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField fullWidth label="Descricao do Setor" value={info.infoAmb?.dscSetor || ''} onChange={(e) => updateInfoAmb('dscSetor', e.target.value)} />
        </Grid>
      </Grid>

      {/* Atividade */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Atividade</Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField fullWidth multiline rows={2} label="Descricao da Atividade *" value={info.infoAtiv?.dscAtivDes || ''} onChange={(e) => updateInfo('infoAtiv', { dscAtivDes: e.target.value })} />
        </Grid>
      </Grid>

      {/* Agentes Nocivos */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Agentes Nocivos *</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addAgNoc}>Adicionar</Button>
      </Box>
      {agNoc.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Adicione pelo menos um agente nocivo.</Typography>
      )}
      {agNoc.map((ag, idx) => (
        <Box key={idx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Agente {idx + 1}</Typography>
            <IconButton size="small" onClick={() => removeAgNoc(idx)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ESocialReferenceAutocomplete tableNumber={24} value={ag.codAgNoc || ''} onChange={(code) => updateAgNoc(idx, 'codAgNoc', code)} label="Agente Nocivo (Tab. 24) *" required size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Avaliacao</InputLabel>
                <Select value={ag.tpAval ?? ''} onChange={(e) => updateAgNoc(idx, 'tpAval', e.target.value || undefined)} label="Tipo Avaliacao">
                  <MenuItem value="">-</MenuItem>
                  <MenuItem value={1}>Qualitativo</MenuItem>
                  <MenuItem value={2}>Quantitativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="Intensidade" type="number" value={ag.intConc ?? ''} onChange={(e) => updateAgNoc(idx, 'intConc', e.target.value ? Number(e.target.value) : undefined)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Utiliza EPC?</InputLabel>
                <Select value={ag.epcEpi?.utilizEPC ?? 0} onChange={(e) => updateAgNocEpcEpi(idx, 'utilizEPC', e.target.value)} label="Utiliza EPC?">
                  <MenuItem value={0}>Nao se aplica</MenuItem>
                  <MenuItem value={1}>Nao implementado</MenuItem>
                  <MenuItem value={2}>Implementado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Utiliza EPI?</InputLabel>
                <Select value={ag.epcEpi?.utilizEPI ?? 0} onChange={(e) => updateAgNocEpcEpi(idx, 'utilizEPI', e.target.value)} label="Utiliza EPI?">
                  <MenuItem value={0}>Nao se aplica</MenuItem>
                  <MenuItem value={1}>Nao eficaz</MenuItem>
                  <MenuItem value={2}>Eficaz</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      ))}

      {/* Responsaveis Tecnicos */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Responsaveis Tecnicos</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addResp}>Adicionar</Button>
      </Box>
      {respReg.map((resp, idx) => (
        <Box key={idx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Responsavel {idx + 1}</Typography>
            <IconButton size="small" onClick={() => removeResp(idx)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="CPF *" value={resp.cpfResp || ''} onChange={(e) => updateResp(idx, 'cpfResp', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="Nome *" value={resp.nmResp || ''} onChange={(e) => updateResp(idx, 'nmResp', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Orgao</InputLabel>
                <Select value={resp.ideOC ?? 1} onChange={(e) => updateResp(idx, 'ideOC', e.target.value)} label="Orgao">
                  <MenuItem value={1}>CRM</MenuItem>
                  <MenuItem value={2}>CREA</MenuItem>
                  <MenuItem value={3}>CRQ</MenuItem>
                  <MenuItem value={4}>Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField fullWidth size="small" label="Numero *" value={resp.nrOC || ''} onChange={(e) => updateResp(idx, 'nrOC', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>UF</InputLabel>
                <Select value={resp.ufOC || ''} onChange={(e) => updateResp(idx, 'ufOC', e.target.value)} label="UF">
                  <MenuItem value="">-</MenuItem>
                  {UF_OPTIONS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default S2240Form;
